import { Button, Card, CardContent } from '@/components/ui';
import { useBankTransactions } from '@/hooks/use-bank-transactions';
import { formatCurrency } from '@/lib/formatters';
import type { BankDirection, BankMatchStatus, BankTransaction } from '@kgc/api-contract';
import { useTranslation } from '@kgc/i18n';
import { DataTable } from '@kgc/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';

import { BankMatchModal } from './components/BankMatchModal';
import { BankTransactionForm } from './components/BankTransactionForm';

const formatDate = (iso: string): string => new Date(iso).toLocaleDateString('hu-HU');

const MATCH_STATUS_OPTIONS: BankMatchStatus[] = [
  'UNMATCHED',
  'PARTIALLY_MATCHED',
  'MATCHED',
  'RECONCILED',
];

/** matchStatus → badge szín-osztály (state-machine vizualizáció). */
const MATCH_STATUS_BADGE: Record<BankMatchStatus, string> = {
  UNMATCHED: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  PARTIALLY_MATCHED: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  MATCHED: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  RECONCILED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
};

/**
 * Bank-import lista (35-kör Fázis-1, L0/L1).
 * Banki tételek listája matchStatus/direction szűrővel + kézi rögzítés.
 * A párosítás-UI (Fázis-2, money-write) külön jön a BE-6 `/match` endpointra.
 */
export function BankImportListPage() {
  const { t } = useTranslation();
  const [matchStatus, setMatchStatus] = useState<BankMatchStatus | 'ALL'>('ALL');
  const [direction, setDirection] = useState<BankDirection | 'ALL'>('ALL');
  const [showForm, setShowForm] = useState(false);
  const [matchTarget, setMatchTarget] = useState<BankTransaction | null>(null);

  const { transactions, total, isLoading, error, refetch } = useBankTransactions({
    ...(matchStatus !== 'ALL' ? { matchStatus } : {}),
    ...(direction !== 'ALL' ? { direction } : {}),
  });

  const columns: ColumnDef<BankTransaction>[] = [
    {
      id: 'bookingDate',
      header: t('bank.column.bookingDate'),
      cell: ({ row }) => (
        <span className="text-gray-600 dark:text-gray-300">
          {formatDate(row.original.bookingDate)}
        </span>
      ),
    },
    {
      id: 'counterparty',
      header: t('bank.column.counterparty'),
      cell: ({ row }) => (
        <>
          <p className="font-medium text-gray-900 dark:text-gray-100">
            {row.original.counterpartyName ?? '-'}
          </p>
          {row.original.memo && (
            <p className="text-xs text-gray-500 dark:text-gray-400">{row.original.memo}</p>
          )}
        </>
      ),
    },
    {
      id: 'amount',
      header: () => <div className="text-right">{t('bank.column.amount')}</div>,
      cell: ({ row }) => {
        const isIn = row.original.direction === 'IN';
        return (
          <div
            className={`text-right font-medium ${
              isIn ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
            }`}
          >
            {isIn ? '+' : '-'}
            {formatCurrency(row.original.amount)}
          </div>
        );
      },
    },
    {
      id: 'matchStatus',
      header: () => <div className="text-center">{t('bank.column.matchStatus')}</div>,
      cell: ({ row }) => (
        <div className="text-center">
          <span
            className={`rounded-full px-2 py-1 text-xs font-medium ${
              MATCH_STATUS_BADGE[row.original.matchStatus]
            }`}
          >
            {t(`bank.matchStatus.${row.original.matchStatus}`)}
          </span>
        </div>
      ),
    },
    {
      id: 'source',
      header: () => <div className="text-center">{t('bank.column.source')}</div>,
      cell: ({ row }) => (
        <div className="text-center text-xs text-muted-foreground">
          {t(`bank.source.${row.original.source}`)}
        </div>
      ),
    },
    {
      id: 'actions',
      header: () => <div className="text-center">{t('bank.match.action')}</div>,
      cell: ({ row }) => {
        const tx = row.original;
        // Párosítható: bejövő (IN) + még nyitott állapot. OUT / MATCHED / RECONCILED → nincs gomb
        // (a BE OUT-only / dupla-match guard a backstop, ha valahogy mégis hívják).
        const matchable =
          tx.direction === 'IN' &&
          (tx.matchStatus === 'UNMATCHED' || tx.matchStatus === 'PARTIALLY_MATCHED');
        if (!matchable) {
          return null;
        }
        return (
          <div className="text-center">
            <Button
              variant="outline"
              onClick={() => setMatchTarget(tx)}
              className="text-xs"
              data-testid={`bank-match-action-${tx.id}`}
            >
              {t('bank.match.action')}
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="min-h-screen kgc-bg">
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {t('bank.title')}
              </h1>
              <p className="text-gray-500 dark:text-gray-400">{t('bank.subtitle')}</p>
            </div>
            <Button
              onClick={() => setShowForm(s => !s)}
              className="bg-primary hover:bg-primary/90"
              data-testid="bank-new-toggle"
            >
              {t('bank.newTransaction')}
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {showForm && (
          <Card className="mb-6">
            <CardContent className="pt-4">
              <BankTransactionForm
                onSuccess={() => {
                  setShowForm(false);
                  void refetch();
                }}
                onCancel={() => setShowForm(false)}
              />
            </CardContent>
          </Card>
        )}

        {/* Szűrők */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-4">
              <select
                data-testid="bank-filter-status"
                className="rounded-md border border-gray-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100"
                value={matchStatus}
                onChange={e => setMatchStatus(e.target.value as BankMatchStatus | 'ALL')}
              >
                <option value="ALL">{t('bank.filter.allStatuses')}</option>
                {MATCH_STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>
                    {t(`bank.matchStatus.${s}`)}
                  </option>
                ))}
              </select>
              <select
                data-testid="bank-filter-direction"
                className="rounded-md border border-gray-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100"
                value={direction}
                onChange={e => setDirection(e.target.value as BankDirection | 'ALL')}
              >
                <option value="ALL">{t('bank.filter.allDirections')}</option>
                <option value="IN">{t('bank.direction.IN')}</option>
                <option value="OUT">{t('bank.direction.OUT')}</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {isLoading && (
          <Card>
            <CardContent className="py-12">
              <div className="text-center text-gray-500 dark:text-gray-400">
                {t('bank.loading')}
              </div>
            </CardContent>
          </Card>
        )}

        {error && (
          <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
            <CardContent className="py-4">
              <div className="text-center text-red-600 dark:text-red-400">
                {t('bank.error')}: {error}
              </div>
            </CardContent>
          </Card>
        )}

        {!isLoading && !error && (
          <Card>
            <CardContent className="p-0">
              <DataTable
                columns={columns}
                data={transactions}
                emptyMessage={
                  matchStatus !== 'ALL' || direction !== 'ALL'
                    ? t('bank.noResultsFiltered')
                    : t('bank.noResults')
                }
                getRowId={tx => tx.id}
              />
              <div className="px-4 py-2 text-xs text-muted-foreground" data-testid="bank-total">
                {t('bank.total')}: {total}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {matchTarget && (
        <BankMatchModal
          transaction={matchTarget}
          onClose={() => setMatchTarget(null)}
          onMatched={() => void refetch()}
        />
      )}
    </div>
  );
}
